/**
 * Frontend-only Activations types & UI conventions.
 *
 * The API contract types (responses, requests, and the API enums) live in the
 * swagger-generated, single-source-of-truth file:
 *   src/types/generated/activations.generated.ts  (never hand-edit; re-run /swagger-gen)
 *
 * This file holds ONLY the things the API does NOT define:
 *  - UI label maps / convenience constants over the generated enums.
 *  - The declarative CustomizationSchema / CustomizationField builder convention.
 *    The backend stores CustomizationSchema/Config/DefaultConfig as opaque jsonb
 *    (typed `unknown` in the generated contract); cms-portal owns the shape that
 *    the form builder renders and that activations-portal reads to render the runner.
 *  - Frontend-only handler convention enums the public API contract doesn't expose.
 *
 * Serialization notes (Athlyn.Shared JsonOptionsExtensions):
 *  - Property names are camelCase.
 *  - Enums serialize as PascalCase STRINGS, e.g. "Draft", "RaffleDraw".
 *  - Nulls are omitted on write; DateTimes are ISO-8601 UTC strings.
 */

// Re-export the API enums from the generated contract so consumers have one import
// surface and there is no duplicate definition.
//
// NOTE: `ActivationType` is widened here (see below) because the generated contract
// has not yet been regenerated to include the new `BlazePod` member. Re-run
// /swagger-gen once the Activations API exposes it; this widening can then be removed.
export type {
  ActivationFamily,
  ActivationStatus,
  ActivationEventStatus,
  ActivationSessionStatus,
  AssetKind,
  GenerationStatus,
  ResultType,
  DeliveryStatus,
  DeliveredVia,
} from '@/types/generated/activations.generated';

import type {
  ActivationType as GeneratedActivationType,
  ActivationFamily,
  AssetKind,
  ActivationMediaType,
  ActivationParticipantResponse as GeneratedActivationParticipantResponse,
} from '@/types/generated/activations.generated';

/**
 * The participant shape the operator UI consumes.
 *
 * The backend `ActivationParticipantResponse` now carries each participant's best
 * `score` (null for non-score activations like photo booth), but the swagger-generated
 * contract has not been regenerated yet. We augment the generated type with the new
 * optional field here so the BlazePod score panel compiles. Once /swagger-gen is re-run,
 * `score` will be on the generated type and this augmentation can be removed (consumers
 * can import directly from the generated file again).
 */
export interface ActivationParticipant extends GeneratedActivationParticipantResponse {
  /** Best recorded score; null for non-score activations (e.g. photo booth). */
  score?: number | null;
}

/**
 * The activation type union the cms-portal consumes.
 *
 * The backend has already shipped the new `BlazePod` activation type (enum value 3,
 * Input family), but the swagger-generated contract still only carries
 * `'RaffleDraw' | 'PhotoBooth'`. Until /swagger-gen is re-run against the updated API,
 * we widen the generated union with the literal here so the rest of cms-portal can
 * reference `BlazePod` with full type safety. Once regenerated, drop this alias and
 * re-export `ActivationType` from the generated file as before.
 */
export type ActivationType = GeneratedActivationType | 'BlazePod';

/**
 * Reads an activation/template's `activationType` as the widened cms-portal union
 * (which includes `BlazePod`). The generated contract types this field with the
 * narrow union, so a direct `=== 'BlazePod'` comparison is a TS "no overlap" error
 * until /swagger-gen is re-run. Funnel such comparisons through this helper; it is
 * a pure widening cast (BlazePod is a real backend value), safe to remove once the
 * generated union includes BlazePod.
 */
export function asActivationType(type: GeneratedActivationType | ActivationType): ActivationType {
  return type as ActivationType;
}

// Re-export so the rest of cms-portal has a single import surface for the
// media-type union (the API enum is the source of truth).
export type { ActivationMediaType } from '@/types/generated/activations.generated';

// ──────────────────────────────────────────────────────────────────────────
// Frontend-only convention enums (NOT part of the public API contract).
// ──────────────────────────────────────────────────────────────────────────

/** Participant identity kind (frontend convention; not in the admin API contract). */
export type ParticipantKind = 'RegisteredUser' | 'ExternalWalkUp';

/** Handler-level config enum (RaffleConfig.Outcome) — a Config convention, not a contract type. */
export type RaffleOutcome = 'PrizeDraw' | 'Leaderboard';

/**
 * PhotoBooth capture mode (handler-level Config convention; lives inside the opaque
 * activation config, so it is NOT in the swagger contract — mirrors the backend
 * `ActivationCaptureMode` string enum).
 *   - Kiosk          — self-service: guest captures their own selfie and generates instantly.
 *   - OperatorCurated — guests register on their phones; an operator photographs them and
 *                       generates from the CMS, optionally holding for approval.
 */
export type ActivationCaptureMode = 'Kiosk' | 'OperatorCurated';

/**
 * PhotoBooth delivery mode (Config convention, mirrors backend `ActivationDeliveryMode`).
 *   - AutoSend        — deliver each participant's settled assets automatically.
 *   - HoldForApproval — hold generations for Athlyn-team review; only approved assets deliver.
 */
export type ActivationDeliveryMode = 'AutoSend' | 'HoldForApproval';

// ──────────────────────────────────────────────────────────────────────────
// UI label maps / convenience constants over the generated enums.
// ──────────────────────────────────────────────────────────────────────────

export const ACTIVATION_TYPES: ActivationType[] = ['RaffleDraw', 'PhotoBooth', 'BlazePod'];
export const ASSET_KINDS: AssetKind[] = ['Logo', 'Scenery', 'ReferenceImage'];

/** UI label maps. */
export const ACTIVATION_TYPE_LABELS: Record<ActivationType, string> = {
  RaffleDraw: 'Raffle / Prize Draw',
  PhotoBooth: 'AI Photo Booth',
  BlazePod: 'Speed Tap (BlazePod)',
};

export const ACTIVATION_FAMILY_LABELS: Record<ActivationFamily, string> = {
  Input: 'Input',
  Generator: 'Generator',
};

/** UI labels for the PhotoBooth capture mode (view-model enum). */
export const CAPTURE_MODE_LABELS: Record<ActivationCaptureMode, string> = {
  Kiosk: 'Kiosk (guest self-service)',
  OperatorCurated: 'Operator-curated (staff photographs guests)',
};

/** UI labels for the PhotoBooth delivery mode (view-model enum). */
export const DELIVERY_MODE_LABELS: Record<ActivationDeliveryMode, string> = {
  AutoSend: 'Auto-send (deliver immediately)',
  HoldForApproval: 'Hold for approval (review before sending)',
};

/** Which family a type belongs to (mirrors backend handler registration). */
export const ACTIVATION_TYPE_FAMILY: Record<ActivationType, ActivationFamily> = {
  RaffleDraw: 'Input',
  PhotoBooth: 'Generator',
  // BlazePod is an operator-recorded score game — Input family, no media generation.
  BlazePod: 'Input',
};

// ──────────────────────────────────────────────────────────────────────────
// CustomizationSchema (free-form on the backend — the convention lives here)
//
// The backend stores CustomizationSchema/Config/DefaultConfig as opaque jsonb
// (typed `unknown` in the generated contract). cms-portal owns the convention for
// the schema shape: a declarative list of editable fields the form builder renders.
// activations-portal reads the same Config to render the runner.
// ──────────────────────────────────────────────────────────────────────────

export type CustomizationFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'color'
  | 'multiline-list';

export interface CustomizationFieldOption {
  value: string;
  label: string;
}

/**
 * A single declarative, bounded customization field. The form builder renders an
 * input for each field; the resulting values become the activation Config / template
 * DefaultConfig document keyed by `key`.
 */
export interface CustomizationField {
  /** Stable key written into the Config document. */
  key: string;
  /** Human label shown in the form. */
  label: string;
  type: CustomizationFieldType;
  /** Logical grouping for the form (branding / copy / layout / settings). */
  group?: CustomizationGroup;
  description?: string;
  required?: boolean;
  /** Default applied when the field is absent from the current config. */
  defaultValue?: unknown;
  /** Options for `select`. */
  options?: CustomizationFieldOption[];
  /** Numeric bounds for `number`. */
  min?: number;
  max?: number;
  /** Placeholder for text inputs. */
  placeholder?: string;
}

export type CustomizationGroup = 'branding' | 'copy' | 'layout' | 'settings';

export const CUSTOMIZATION_GROUP_LABELS: Record<CustomizationGroup, string> = {
  branding: 'Branding',
  copy: 'Copy & Text',
  layout: 'Layout',
  settings: 'Type Settings',
};

/** The declarative customization schema document authored on a template. */
export interface CustomizationSchema {
  /** Schema version, for forward-compat. */
  version?: number;
  fields: CustomizationField[];
}

/** A config / default-config document is just a flat key→value bag. */
export type ActivationConfigValues = Record<string, unknown>;

// ──────────────────────────────────────────────────────────────────────────
// REQUIRED, strongly-typed per-activation-type config.
//
// The backend stores the activation's config as `ActivationConfig.Values` — a
// JSON document deserialized by the type handler into a strongly-typed config
// (RaffleConfig / PhotoBoothConfig). The Attach/Customize request models carry a
// `config` of the ActivationConfig shape: the typed object goes INSIDE `values`.
//
// These mirror the backend CLR configs EXACTLY (camelCase property names, string
// enums) so the document round-trips through the handler's ValidateConfigAsync
// (which calls Require<T> and would 400 on a missing/invalid document).
//   - RaffleConfig:     athlyn-core …/Configs/RaffleConfig.cs
//   - PhotoBoothConfig: athlyn-core …/Configs/PhotoBoothConfig.cs
//   - ActivationConfig: athlyn-core …/Json/ActivationConfig.cs  ({ values })
//
// These are NOT in the swagger contract (the API types `config` as opaque
// `unknown`), so they live here and are owned by cms-portal + activations-portal.
// ──────────────────────────────────────────────────────────────────────────

/**
 * Strongly-typed BlazePod config — matches backend BlazePodConfig.
 * BlazePod is an operator-recorded speed-tap score game (Input family). The operator
 * records each registered guest's successful-tap count over `durationSeconds`; a nearby
 * screen shows a branded top-`leaderboardSize` leaderboard.
 */
export interface BlazePodConfig {
  /** Length of one round, in seconds (default 30). */
  durationSeconds: number;
  /** How many top scores the leaderboard screen shows (default 10). */
  leaderboardSize: number;
}

/** Strongly-typed RaffleDraw config — matches backend RaffleConfig. */
export interface RaffleConfig {
  outcome: RaffleOutcome;
  numberOfWinners: number;
  prizeName?: string | null;
  prizeNameAr?: string | null;
  captureName: boolean;
  captureEmail: boolean;
}

/**
 * One fully self-describing output in a PhotoBooth activation. Each variant produces
 * one generation → one asset. Mirrors the backend `PhotoBoothVariant` inside
 * `PhotoBoothConfig.Variants` (camelCase, string enums).
 */
export interface PhotoBoothVariant {
  /** Image | Video — drives the generation media type and how the asset renders. */
  mediaType: ActivationMediaType;
  provider: string;
  model: string;
  /** Provider task type (e.g. "gemini-2.5-flash-image", "seedance-2-fast"). */
  taskType: string;
  /** Optional aspect ratio hint (e.g. "1:1", "16:9"). */
  aspectRatio?: string;
  prompt: string;
  /**
   * Two-step VIDEO pipeline (Gemini → Kling). For a Video variant, Gemini first
   * composes a still ("pre-video image") from the captured photo + assets using
   * this prompt, then the video model (Kling) animates that composite. The main
   * `prompt` above is the Kling animation/video prompt. These map 1:1 to the
   * backend PhotoBoothVariant camelCase fields and live inside the opaque config.
   */
  preVideoPrompt?: string;
  /** Pre-video still model (defaults to "gemini" for video variants). */
  preVideoModel?: string;
  /** Pre-video still task type (defaults to "gemini-2.5-flash-image"). */
  preVideoTaskType?: string;
  /**
   * Optional preconfigured social caption shared alongside the photo/video from the
   * participant's landing page (Instagram, Facebook, LinkedIn). Maps to the backend
   * `PhotoBoothVariant.ShareCaption`; lives inside the opaque config (camelCase, flat).
   * No validation requirement — absent caption is fine.
   */
  shareCaption?: string;
  /**
   * Optional negative prompt — describes what the model should AVOID. Only meaningful for models
   * whose capability catalog entry declares `supportsNegativePrompt` (the editor gates the field
   * on that flag); a model that does not accept one drops the value on selection. Maps to the
   * backend `PhotoBoothVariant.NegativePrompt`; lives inside the opaque config (camelCase, flat).
   * Absent when unset ("negativePrompt only when set"), so an incapable model round-trips clean.
   */
  negativePrompt?: string;
}

/** Strongly-typed PhotoBooth config — matches backend PhotoBoothConfig. */
export interface PhotoBoothConfig {
  /**
   * Ordered list of variants — the source of truth for what gets generated.
   * Each variant fully describes its media type, provider, model, task type and prompt.
   */
  variants: PhotoBoothVariant[];
  /**
   * @deprecated Back-compat default provider for legacy (Prompts-only) configs.
   * New configs author provider per variant.
   */
  provider: string;
  /**
   * @deprecated Back-compat default model for legacy (Prompts-only) configs.
   */
  model: string;
  /**
   * @deprecated Back-compat flat prompt list. Legacy configs are read into Image
   * variants; new configs keep this empty and use `variants`.
   */
  prompts: string[];
  generationCount: number;
  inputAssetIds: string[];
  pollIntervalMs: number;
  maxPollAttempts: number;
  instructions?: string | null;
  instructionsAr?: string | null;
  /** How the photo is captured (defaults to Kiosk for the unchanged self-service flow). */
  captureMode: ActivationCaptureMode;
  /** How assets are delivered (only meaningful in OperatorCurated; defaults to AutoSend). */
  deliveryMode: ActivationDeliveryMode;
}

// ──────────────────────────────────────────────────────────────────────────
// Curated PiAPI model list — historically the Model dropdown options for variants.
// As of the media-generation catalog work (ADR-0036) the dropdown is sourced from the
// API-served capability catalog instead; this constant survives ONLY as the resilience
// FALLBACK (see `@/hooks/useMediaGenerationCatalog` → `PIAPI_MODELS_FALLBACK`) so a catalog
// outage cannot brick the config form, and as the seed for `defaultPhotoBoothVariant()`.
// Picking an entry sets provider+model+taskType together; the 'custom' sentinel reveals
// free-text model/taskType inputs. Filtered by the variant's MediaType.
// ──────────────────────────────────────────────────────────────────────────

export interface PiApiModelOption {
  /** Human label shown in the dropdown. */
  label: string;
  /** Which media type this model produces. */
  mediaType: ActivationMediaType;
  provider: string;
  model: string;
  taskType: string;
  /** Default aspect ratio applied to the variant when this model is picked. */
  aspectRatio?: string;
}

export const PIAPI_MODELS: PiApiModelOption[] = [
  {
    label: 'Gemini 2.5 Flash Image',
    mediaType: 'Image',
    provider: 'piapi',
    model: 'gemini',
    taskType: 'gemini-2.5-flash-image',
    aspectRatio: '1:1',
  },
  {
    // Both video models run the SAME two-step pipeline: Gemini composes a still from the
    // captured photo + assets (preVideoPrompt), then the video model animates that composite (prompt).
    // Kling accepts a photorealistic composite of a real person.
    label: 'Kling (video)',
    mediaType: 'Video',
    provider: 'piapi',
    model: 'kling',
    taskType: 'video_generation',
    aspectRatio: '9:16',
  },
  {
    // Seedance (less-restriction tier) also animates the composite, but its moderation rejects
    // realistic human faces — so the Seedance pre-video prompt should STYLIZE the person
    // (cartoon/3D) and the video prompt can push it back toward realism.
    label: 'Seedance (video)',
    mediaType: 'Video',
    provider: 'piapi',
    model: 'seedance',
    taskType: 'seedance-2-fast-less-restriction',
    aspectRatio: '9:16',
  },
];

/**
 * Default pre-video (Gemini) still model + task type for a new video variant.
 * The captured portrait + assets are composed into a still that Kling then animates.
 */
export const DEFAULT_PRE_VIDEO_MODEL = 'gemini';
export const DEFAULT_PRE_VIDEO_TASK_TYPE = 'gemini-2.5-flash-image';

/**
 * Generic default pre-video prompt seeded when a variant first becomes Video.
 * Operators edit this per activation (e.g. to describe a specific costume/scene/brand).
 */
export const DEFAULT_PRE_VIDEO_PROMPT =
  'Using the supplied portrait as the face reference, transform this person into [describe costume/scene]. ' +
  'Apply the [brand] logo from the supplied reference image accurately onto the clothing and any props. ' +
  "Keep the person's facial features, skin tone, and likeness faithful to the original portrait. " +
  'Photorealistic, sharp focus, cinematic, high detail.';

/** Sentinel value for the "Custom…" free-text option in the model dropdown. */
export const PIAPI_CUSTOM_MODEL = 'custom';

/** UI labels for the variant media type. */
export const MEDIA_TYPE_LABELS: Record<ActivationMediaType, string> = {
  Image: 'Image',
  Video: 'Video',
};

/** A brand-new default variant (one Image Gemini variant). */
export function defaultPhotoBoothVariant(): PhotoBoothVariant {
  const gemini = PIAPI_MODELS[0];
  return {
    mediaType: gemini.mediaType,
    provider: gemini.provider,
    model: gemini.model,
    taskType: gemini.taskType,
    aspectRatio: '',
    prompt: '',
  };
}

/**
 * Returns the preVideo* fields a Video variant should carry. Seeds the Gemini
 * pre-model + task type, and a generic default prompt when none is supplied yet.
 * Existing (non-empty) preVideoPrompt is preserved.
 */
export function seedVideoVariantDefaults(
  variant: Partial<PhotoBoothVariant>,
): Pick<PhotoBoothVariant, 'preVideoPrompt' | 'preVideoModel' | 'preVideoTaskType'> {
  return {
    preVideoModel: variant.preVideoModel?.trim() ? variant.preVideoModel : DEFAULT_PRE_VIDEO_MODEL,
    preVideoTaskType: variant.preVideoTaskType?.trim()
      ? variant.preVideoTaskType
      : DEFAULT_PRE_VIDEO_TASK_TYPE,
    preVideoPrompt: variant.preVideoPrompt?.trim()
      ? variant.preVideoPrompt
      : DEFAULT_PRE_VIDEO_PROMPT,
  };
}

/** Union of the typed configs the editor produces. */
export type TypedActivationConfig = RaffleConfig | PhotoBoothConfig | BlazePodConfig;

/**
 * The request `config` shape (entity-layer ActivationConfig). The typed config
 * document is nested under `values`, exactly as the backend (de)serializes it.
 */
export interface ActivationConfigEnvelope<T = TypedActivationConfig> {
  values: T;
}

/** Defaults for a brand-new BlazePod config (mirror backend field defaults). */
export function defaultBlazePodConfig(): BlazePodConfig {
  return {
    durationSeconds: 30,
    leaderboardSize: 10,
  };
}

/** Defaults for a brand-new RaffleDraw config (mirror backend field defaults). */
export function defaultRaffleConfig(): RaffleConfig {
  return {
    outcome: 'PrizeDraw',
    numberOfWinners: 1,
    prizeName: '',
    prizeNameAr: '',
    captureName: false,
    captureEmail: false,
  };
}

/** Defaults for a brand-new PhotoBooth config (mirror backend field defaults). */
export function defaultPhotoBoothConfig(): PhotoBoothConfig {
  return {
    variants: [defaultPhotoBoothVariant()],
    provider: 'piapi',
    model: '',
    prompts: [],
    generationCount: 1,
    inputAssetIds: [],
    pollIntervalMs: 2000,
    maxPollAttempts: 60,
    instructions: '',
    instructionsAr: '',
    captureMode: 'Kiosk',
    deliveryMode: 'AutoSend',
  };
}

/**
 * Coerces a loaded `values.variants` array into typed PhotoBoothVariant objects.
 * Returns [] when the source has no usable variants.
 */
function readVariants(src: Record<string, unknown>): PhotoBoothVariant[] {
  if (!Array.isArray(src.variants)) return [];
  return (src.variants as unknown[])
    .filter((v): v is Record<string, unknown> => !!v && typeof v === 'object')
    .map((v) => {
      const variant: PhotoBoothVariant = {
        mediaType: v.mediaType === 'Video' ? 'Video' : 'Image',
        provider: typeof v.provider === 'string' ? v.provider : '',
        model: typeof v.model === 'string' ? v.model : '',
        taskType: typeof v.taskType === 'string' ? v.taskType : '',
        aspectRatio: typeof v.aspectRatio === 'string' ? v.aspectRatio : '',
        prompt: typeof v.prompt === 'string' ? v.prompt : '',
      };
      // Back-compat: preVideo* are optional and only present on two-step video variants.
      if (typeof v.preVideoPrompt === 'string') variant.preVideoPrompt = v.preVideoPrompt;
      if (typeof v.preVideoModel === 'string') variant.preVideoModel = v.preVideoModel;
      if (typeof v.preVideoTaskType === 'string') variant.preVideoTaskType = v.preVideoTaskType;
      // Optional preconfigured social caption (Instagram/Facebook/LinkedIn share text).
      if (typeof v.shareCaption === 'string') variant.shareCaption = v.shareCaption;
      // Optional negative prompt (only present on models that accept one).
      if (typeof v.negativePrompt === 'string') variant.negativePrompt = v.negativePrompt;
      return variant;
    });
}

/**
 * Builds a typed config for the given activation type, seeded from the type
 * defaults and overlaid with any canonical keys present on a prior config or the
 * template's DefaultConfig. `source` is read as the raw `values` document (the
 * same canonical camelCase keys), so it round-trips a template DefaultConfig or
 * an existing activation.config.values without slugifying anything.
 */
export function buildTypedConfig(
  type: ActivationType,
  source?: unknown,
): TypedActivationConfig {
  const src = (source && typeof source === 'object' ? (source as Record<string, unknown>) : {});
  if (type === 'BlazePod') {
    const base = defaultBlazePodConfig();
    return {
      durationSeconds:
        typeof src.durationSeconds === 'number' ? src.durationSeconds : base.durationSeconds,
      leaderboardSize:
        typeof src.leaderboardSize === 'number' ? src.leaderboardSize : base.leaderboardSize,
    };
  }
  if (type === 'RaffleDraw') {
    const base = defaultRaffleConfig();
    return {
      outcome: src.outcome === 'Leaderboard' ? 'Leaderboard' : base.outcome,
      numberOfWinners:
        typeof src.numberOfWinners === 'number' ? src.numberOfWinners : base.numberOfWinners,
      prizeName: typeof src.prizeName === 'string' ? src.prizeName : base.prizeName,
      prizeNameAr: typeof src.prizeNameAr === 'string' ? src.prizeNameAr : base.prizeNameAr,
      captureName: typeof src.captureName === 'boolean' ? src.captureName : base.captureName,
      captureEmail: typeof src.captureEmail === 'boolean' ? src.captureEmail : base.captureEmail,
    };
  }
  // PhotoBooth
  const base = defaultPhotoBoothConfig();
  const topProvider =
    typeof src.provider === 'string' && src.provider ? src.provider : base.provider;
  const topModel = typeof src.model === 'string' ? src.model : base.model;
  const legacyPrompts = Array.isArray(src.prompts) ? (src.prompts as unknown[]).map(String) : [];

  // Variants are the source of truth. Back-compat: if a loaded config has prompts
  // but no variants, synthesize one Image variant per prompt using the top-level
  // provider/model so legacy activations display correctly. A brand-new config
  // (no variants, no prompts) gets the default single Image variant.
  let variants = readVariants(src);
  if (variants.length === 0) {
    if (legacyPrompts.length > 0) {
      variants = legacyPrompts.map((prompt) => ({
        mediaType: 'Image' as ActivationMediaType,
        provider: topProvider,
        model: topModel,
        taskType: '',
        aspectRatio: '',
        prompt,
      }));
    } else {
      variants = base.variants;
    }
  }

  return {
    variants,
    provider: topProvider,
    model: topModel,
    prompts: legacyPrompts,
    generationCount:
      typeof src.generationCount === 'number' ? src.generationCount : base.generationCount,
    inputAssetIds: Array.isArray(src.inputAssetIds)
      ? (src.inputAssetIds as unknown[]).map(String)
      : base.inputAssetIds,
    pollIntervalMs:
      typeof src.pollIntervalMs === 'number' ? src.pollIntervalMs : base.pollIntervalMs,
    maxPollAttempts:
      typeof src.maxPollAttempts === 'number' ? src.maxPollAttempts : base.maxPollAttempts,
    instructions: typeof src.instructions === 'string' ? src.instructions : base.instructions,
    instructionsAr:
      typeof src.instructionsAr === 'string' ? src.instructionsAr : base.instructionsAr,
    captureMode:
      src.captureMode === 'OperatorCurated' ? 'OperatorCurated' : base.captureMode,
    deliveryMode:
      src.deliveryMode === 'HoldForApproval' ? 'HoldForApproval' : base.deliveryMode,
  };
}

/**
 * Reads the raw `values` document out of either an existing activation.config or a
 * template.defaultConfig. Both store the canonical document under `values`; some
 * older/template documents may store it flat — accept both so prefill is robust.
 */
export function readConfigValues(config: unknown): unknown {
  if (config && typeof config === 'object') {
    const obj = config as Record<string, unknown>;
    if ('values' in obj && obj.values && typeof obj.values === 'object') {
      return obj.values;
    }
    return obj;
  }
  return undefined;
}

// The attach/customize request `config` field carries the FLAT typed config values.
// The backend wraps it into ActivationConfig.Values; resolve + ActivationResponse return
// the flat values back (source.Config?.Values), so write and read are symmetric (flat).
export function toConfigEnvelope<T extends TypedActivationConfig>(values: T): T {
  return values;
}

/** A field→message map of client-side validation errors (empty = valid). */
export type ConfigErrors = Record<string, string>;

/** Validates a typed config; returns inline field errors (empty object = valid). */
export function validateTypedConfig(
  type: ActivationType,
  config: TypedActivationConfig,
): ConfigErrors {
  const errors: ConfigErrors = {};
  if (type === 'BlazePod') {
    const blaze = config as BlazePodConfig;
    if (!blaze.durationSeconds || blaze.durationSeconds < 1) {
      errors.durationSeconds = 'The round duration must be at least one second.';
    }
    if (!blaze.leaderboardSize || blaze.leaderboardSize < 1) {
      errors.leaderboardSize = 'The leaderboard must show at least one entry.';
    }
    return errors;
  }
  if (type === 'RaffleDraw') {
    const raffle = config as RaffleConfig;
    if (raffle.outcome !== 'PrizeDraw' && raffle.outcome !== 'Leaderboard') {
      errors.outcome = 'An outcome is required.';
    }
    if (raffle.outcome === 'PrizeDraw' && (!raffle.numberOfWinners || raffle.numberOfWinners < 1)) {
      errors.numberOfWinners = 'A prize draw must have at least one winner.';
    }
  } else {
    const booth = config as PhotoBoothConfig;
    if (!booth.variants || booth.variants.length === 0) {
      errors.variants = 'At least one variant is required.';
    } else {
      booth.variants.forEach((variant, index) => {
        if (!variant.model?.trim()) {
          errors[`variant-${index}-model`] = 'A model is required.';
        }
        if (!variant.prompt?.trim()) {
          errors[`variant-${index}-prompt`] =
            variant.mediaType === 'Video' ? 'A video prompt is required.' : 'A prompt is required.';
        }
        // A Video variant runs the two-step Gemini→Kling pipeline, which also needs
        // the pre-video (Gemini) image prompt.
        if (variant.mediaType === 'Video' && !variant.preVideoPrompt?.trim()) {
          errors[`variant-${index}-preVideoPrompt`] = 'A pre-video image prompt is required.';
        }
      });
    }
    if (!booth.generationCount || booth.generationCount < 1) {
      errors.generationCount = 'The generation count must be at least one.';
    }
  }
  return errors;
}
